import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useSettings } from "../context/SettingsContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { LayoutDashboard, PackageOpen, Users, ClipboardList, Settings as SettingsIcon, LogOut, User, Menu, X, Globe } from "lucide-react";

function BrandLogo() {
  const { logoUrl } = useSettings();
  const { t } = useLang();
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt="Logo"
        data-testid="brand-logo-image"
        className="h-9 w-auto object-contain"
      />
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid="brand-logo-text">
      <div className="w-9 h-9 bg-[#0055FF] flex items-center justify-center text-white font-bold text-lg" style={{ borderRadius: 4 }}>
        R
      </div>
      <div className="leading-tight hidden sm:block">
        <div className="font-bold text-sm tracking-tight">{t("appName")}</div>
        <div className="label-mini">{t("appSub")}</div>
      </div>
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/returns", label: t("nav.returns"), icon: PackageOpen, testid: "nav-returns" },
  ];
  if (user?.role === "admin") {
    navItems.push(
      { to: "/users", label: t("nav.users"), icon: Users, testid: "nav-users" },
      { to: "/audit", label: t("nav.audit"), icon: ClipboardList, testid: "nav-audit" },
      { to: "/settings", label: t("nav.settings"), icon: SettingsIcon, testid: "nav-settings" }
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header
      data-testid="global-header"
      className="sticky top-0 z-40 bg-white border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/dashboard" className="flex items-center gap-3 shrink-0" data-testid="header-brand-link">
          <BrandLogo />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  isActive ? "text-[#0055FF] bg-[#E6EFFF]" : "text-muted-foreground hover:text-foreground"
                }`
              }
              style={{ borderRadius: 4 }}
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "de" ? "en" : "de")}
            className="text-xs font-bold tracking-widest text-muted-foreground hover:text-foreground px-2 py-1 border border-border"
            style={{ borderRadius: 4 }}
            aria-label="Sprache umschalten"
          >
            <Globe className="w-3.5 h-3.5 inline mr-1" />
            {lang.toUpperCase()}
          </button>

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu-trigger" className="hidden sm:inline-flex">
                  <User className="w-4 h-4 mr-1.5" />
                  <span className="max-w-[100px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="w-4 h-4 mr-2" /> {t("nav.profile")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 border border-border"
            style={{ borderRadius: 4 }}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-white" data-testid="mobile-nav">
          <nav className="px-4 py-2 flex flex-col gap-1">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`${it.testid}-mobile`}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm font-medium flex items-center gap-2 ${
                    isActive ? "text-[#0055FF] bg-[#E6EFFF]" : "text-muted-foreground"
                  }`
                }
                style={{ borderRadius: 4 }}
              >
                <it.icon className="w-4 h-4" /> {it.label}
              </NavLink>
            ))}
            <div className="border-t border-border my-2" />
            {user && (
              <>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    navigate("/profile");
                  }}
                  data-testid="menu-profile-mobile"
                  className="px-3 py-3 text-sm font-medium flex items-center gap-2 text-left text-muted-foreground"
                >
                  <User className="w-4 h-4" /> {t("nav.profile")}
                </button>
                <button
                  onClick={handleLogout}
                  data-testid="menu-logout-mobile"
                  className="px-3 py-3 text-sm font-medium flex items-center gap-2 text-left text-destructive"
                >
                  <LogOut className="w-4 h-4" /> {t("nav.logout")}
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
